// Story 2.2 — query hook for GET /api/v1/admin/portfolios.
//
// Backs the admin list page at /admin/portfolios. Status + kind filters
// + pagination (peek-ahead has_next).

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type PortfolioRecruitingStatus =
  | "recruiting"
  | "composed"
  | "stalled"
  | "retired";
export type PortfolioKind = "regular" | "special" | "welcome";

export type AdminPortfolioListFilters = {
  recruiting_status?: PortfolioRecruitingStatus | "any";
  kind?: PortfolioKind | "any";
  page: number;
  page_size: number;
};

export const DEFAULT_ADMIN_PORTFOLIO_FILTERS: AdminPortfolioListFilters = {
  recruiting_status: "any",
  kind: "any",
  page: 1,
  page_size: 50,
};

/** Certificate-TIER probabilities, as fractions summing to 1.0 ± 0.001.
 *
 *  Hangs off the PORTFOLIO, never off a creator: one dossier, one set of
 *  odds, identical no matter which creator the draw returns. There is no
 *  per-creator equivalent anywhere in the API, the composer, or the
 *  schema — `portfolio_creators.tier_overrides` was dropped rather than
 *  merely ignored, so it cannot be quietly re-wired. */
export type TierDistribution = {
  common: number;
  preferred: number;
  premium: number;
  founders: number;
};

export type AdminPortfolioListRow = {
  id: string;
  title: string;
  theme: string | null;
  kind: PortfolioKind;
  recruiting_status: PortfolioRecruitingStatus;
  recruiting_deadline: string | null;
  target_creator_count: number | null;
  creator_count: number;
  created_at: string;
  /** NULL means "use system_config.tier_distribution_defaults" — the
   *  platform-wide odds. Same semantics the column carries. */
  tier_distribution: TierDistribution | null;
};

export type AdminPortfoliosListResponse = {
  data: {
    items: AdminPortfolioListRow[];
    page: number;
    page_size: number;
    has_next: boolean;
  };
};

function buildQueryString(f: AdminPortfolioListFilters): string {
  const params = new URLSearchParams();
  if (f.recruiting_status && f.recruiting_status !== "any") {
    params.set("recruiting_status", f.recruiting_status);
  }
  if (f.kind && f.kind !== "any") {
    params.set("kind", f.kind);
  }
  params.set("page", String(f.page));
  params.set("page_size", String(f.page_size));
  return params.toString();
}

async function fetchAdminPortfolios(
  filters: AdminPortfolioListFilters,
): Promise<AdminPortfoliosListResponse> {
  const qs = buildQueryString(filters);
  const response = await fetch(`/api/v1/admin/portfolios?${qs}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin portfolios list fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminPortfoliosListResponse;
}

export function useAdminPortfolios(filters: AdminPortfolioListFilters) {
  return useQuery<AdminPortfoliosListResponse, Error>({
    queryKey: ["admin-portfolios", filters],
    queryFn: () => fetchAdminPortfolios(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
